import {
  createEmployeeDependentsIncorporation,
  createInsuranceDependentsIncorporation,
  employeeActiveCoverages,
  getTicketV2,
  getTicketAvailableCoverages,
  getDependentsIncorporationForm,
  patchTicketStatus,
  putDocumentOfDependentsIncorporationForm,
  resendDependentsIncorporationForm,
  resendDependentsIncorporationNoDeliverableForm,
  getPoliciesForDependentsIncorporation,
  getCompaniesForDependentsIncorporation,
  getDependentsIncorporationFormEmployee
} from '../../../../network';

import {
  POST_EMPLOYEE_DEPENDENTS_INCORPORATION,
  POST_INSURANCE_DEPENDENTS_INCORPORATION,
  POST_RESEND_DEPENDENTS_INCORPORATION_FORM,
  POST_RESEND_DEPENDENTS_INCORPORATION_NO_DELIVERABLE_FORM,
  POST_SEND_TO_BROKER_DEPENDENTS_INCORPORATION_FORM,
  POST_SEND_TO_INSURANCE_DEPENDENTS_INCORPORATION,
  POST_CANCEL_DEPENDENTS_INCORPORATION,
  POST_FINISH_DEPENDENTS_INCORPORATION,
  PUT_DOCUMENT_OF_DEPENDENTS_INCORPORATION,
  GET_DEPENDENTS_INCORPORATION_FORM,
  FETCH_DEPENDENTS_INCORPORATION_FORM,
  SET_DEPENDENTS_INCORPORATION_FORM,
  FETCH_POLICIES_FOR_DEPENDENTS_INCORPORATION_FORM,
  FETCH_COMPANIES_FOR_DEPENDENTS_INCORPORATION_FORM,
  FETCH_EMPLOYEE_ACTIVE_COVERAGES_FOR_DEPENDENTS_INCORPORATION_FORM,
  FETCH_DEPENDENTS_INCORPORATION_FORM_EMPLOYEE
} from '@/store/types';

const store = {};

const getDefaultState = () => ({
  form: {}
});

store.state = getDefaultState();

store.getters = {
  [GET_DEPENDENTS_INCORPORATION_FORM](state) {
    return state.form;
  }
};

store.mutations = {
  [SET_DEPENDENTS_INCORPORATION_FORM](state, form) {
    state.form = form;
  }
};

const getTicketIdFromToken = (token) => {
  try {
    const decodedToken = JSON.parse(atob(token.split('.')[1]));
    return decodedToken?.tid || null;
  } catch (error) {
    return null;
  }
};

const mapTicketStateLabel = (ticket) => {
  const stateId = ticket?.stateId ?? ticket?.state?.id;
  if (stateId === 1) return 'Creado';
  if (stateId === 3) return 'Ejecutado';
  if (stateId === 4) return 'Cancelado';
  if (stateId === 7) return 'En corredora';
  if (stateId === 8) return 'En aseguradora';
  return (
    ticket?.state?.state || ticket?.state?.name || ticket?.state?.tag || ''
  );
};

const normalizeInsurances = (ticket) => {
  const candidateInsurances =
    ticket?.insurances ||
    ticket?.insuranceCompanies ||
    ticket?.ticket?.insurances ||
    [];

  if (!Array.isArray(candidateInsurances)) return [];

  return candidateInsurances.map((insurance) => ({
    id: insurance?.id,
    businessName: insurance?.businessName || insurance?.name || '-',
    files: Array.isArray(insurance?.files) ? insurance.files : []
  }));
};

store.actions = {
  async [POST_EMPLOYEE_DEPENDENTS_INCORPORATION](_, payload) {
    const response = await createEmployeeDependentsIncorporation(payload);
    return response.data;
  },
  async [POST_INSURANCE_DEPENDENTS_INCORPORATION](_, payload) {
    const response = await createInsuranceDependentsIncorporation(payload);
    return response.data;
  },
  async [FETCH_DEPENDENTS_INCORPORATION_FORM]({ commit }, token) {
    const ticketId = getTicketIdFromToken(token);
    if (!ticketId) {
      throw new Error('Invalid collaborator token: missing ticket id.');
    }

    const [ticketResponse, coveragesResponse, formResponse] = await Promise.all(
      [
        getTicketV2(ticketId, token),
        getTicketAvailableCoverages(ticketId, token).catch(() => ({
          data: []
        })),
        getDependentsIncorporationForm(token)
      ]
    );

    const ticket = ticketResponse.data || {};
    const normalizedForm = {
      id: ticket.id,
      ticketId: ticket.id,
      stateId: ticket.stateId ?? ticket?.state?.id,
      state: mapTicketStateLabel(ticket),
      isTokenExpired: Boolean(formResponse.data?.isTokenExpired),
      ticket,
      company: ticket.company,
      employee: ticket.employee,
      insurances: normalizeInsurances(formResponse.data),
      coverages: Array.isArray(coveragesResponse?.data)
        ? coveragesResponse.data
        : []
    };

    commit(SET_DEPENDENTS_INCORPORATION_FORM, normalizedForm);
    return normalizedForm;
  },
  async [PUT_DOCUMENT_OF_DEPENDENTS_INCORPORATION](_, { token, payload }) {
    const response = await putDocumentOfDependentsIncorporationForm({
      token,
      payload
    });
    return response.data;
  },
  async [POST_RESEND_DEPENDENTS_INCORPORATION_FORM](_, token) {
    const response = await resendDependentsIncorporationForm(token);
    return response.data;
  },
  async [POST_RESEND_DEPENDENTS_INCORPORATION_NO_DELIVERABLE_FORM](_, token) {
    const response =
      await resendDependentsIncorporationNoDeliverableForm(token);
    return response.data;
  },
  async [POST_SEND_TO_BROKER_DEPENDENTS_INCORPORATION_FORM](
    _,
    { ticketId, token, status = 7 }
  ) {
    const response = await patchTicketStatus(ticketId, { status }, token);
    return response.data;
  },
  async [POST_CANCEL_DEPENDENTS_INCORPORATION](_, { ticketId, status = 4 }) {
    const response = await patchTicketStatus(ticketId, { status });
    return response.data;
  },
  async [POST_SEND_TO_INSURANCE_DEPENDENTS_INCORPORATION](
    _,
    { ticketId, status = 8 }
  ) {
    const response = await patchTicketStatus(ticketId, { status });
    return response.data;
  },
  async [POST_FINISH_DEPENDENTS_INCORPORATION](_, { ticketId, status = 3 }) {
    const response = await patchTicketStatus(ticketId, { status });
    return response.data;
  },
  async [FETCH_POLICIES_FOR_DEPENDENTS_INCORPORATION_FORM](_, rut) {
    const response = await getPoliciesForDependentsIncorporation(rut);
    return response.data;
  },
  async [FETCH_COMPANIES_FOR_DEPENDENTS_INCORPORATION_FORM](_, rut) {
    const response = await getCompaniesForDependentsIncorporation(rut);
    return response.data;
  },
  async [FETCH_EMPLOYEE_ACTIVE_COVERAGES_FOR_DEPENDENTS_INCORPORATION_FORM](
    _,
    { employeeId, allowDependents = true }
  ) {
    const response = await employeeActiveCoverages(employeeId, allowDependents);
    return response.data;
  },
  async [FETCH_DEPENDENTS_INCORPORATION_FORM_EMPLOYEE](_, token) {
    return new Promise((resolve, reject) => {
      getDependentsIncorporationFormEmployee(token)
        .then((resp) => {
          resolve(resp);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }
};

export default store;
